import React from "react";
import { Link } from "react-router-dom";
import { useApp } from "../../store";
import { useHomeData, HeroVideo } from "../../homeData";
import { LearnSection, InstagramSection, BlogSection } from "./V1";
import { ArrowRight } from "lucide-react";

/* V2 — Showroom Premium. Split hero, refined microinteractions, carbon close-ups. */
export default function V2() {
  const { t } = useApp();
  const { products, blog, igPosts } = useHomeData();

  return (
    <main className="bg-[#FAFAF7]" data-testid="home-v2">
      {/* HERO split */}
      <section className="relative min-h-screen pt-24 pb-12">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-5 fade-up">
            <div className="overline mb-6">{t.hero.overline}</div>
            <h1 className="font-serif-display text-6xl md:text-7xl lg:text-8xl leading-[0.95] text-slate-900">{t.hero.title}</h1>
            <p className="font-body text-lg text-slate-600 max-w-md mt-8 leading-relaxed">{t.hero.subtitle}</p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/products/efoils" className="btn-primary">{t.hero.cta1}</Link>
              <Link to="/book" className="btn-ghost">{t.hero.cta2}</Link>
            </div>
            <div className="mt-16 grid grid-cols-3 gap-4 max-w-md">
              <Stat k="Weight" v="24kg" />
              <Stat k="Top Speed" v="62 km/h" />
              <Stat k="Range" v="3hr" />
            </div>
          </div>
          <div className="lg:col-span-7 relative">
            <div className="relative aspect-[16/12] rounded-2xl overflow-hidden shadow-2xl shadow-sky-500/20">
              <HeroVideo overlay="linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.0) 100%)" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-white border border-slate-200 px-6 py-4 rounded-md shadow-xl">
              <div className="overline">Live</div>
              <div className="font-serif-display text-2xl text-slate-900">Hydrofoil flight</div>
            </div>
          </div>
        </div>
      </section>

      {/* USPs */}
      <section className="py-24 px-6 md:px-12 max-w-[1440px] mx-auto">
        <div className="overline mb-3">{t.usps.overline}</div>
        <h2 className="font-serif-display text-4xl md:text-6xl text-slate-900 max-w-2xl">{t.usps.title}</h2>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
          <USPCard num="01" title={t.usps.f1t} sub="vs 35kg comp." body={t.usps.f1d} highlight />
          <USPCard num="02" title="6s" sub="Battery swap" body={t.usps.f2d} />
          <USPCard num="03" title="0" sub="Tools needed" body={t.usps.f3d} />
        </div>
      </section>

      {/* Product showcase grid */}
      <section className="py-24 px-6 md:px-12 max-w-[1440px] mx-auto">
        <div className="flex items-end justify-between mb-14">
          <div>
            <div className="overline mb-3">{t.shop.overline}</div>
            <h2 className="font-serif-display text-5xl md:text-6xl text-slate-900">{t.shop.title}</h2>
          </div>
          <Link to="/products/efoils" className="hidden md:flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-ui text-slate-700 hover:text-sky-500">{t.shop.viewAll} <ArrowRight size={14} /></Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {products.slice(0, 3).map((p) => (
            <Link to={`/product/${p.slug}`} key={p.id} className="group card-clean overflow-hidden block" data-testid={`v2-prod-${p.slug}`}>
              <div className="aspect-[4/5] overflow-hidden bg-slate-50">
                <img src={p.images?.[0]} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="p-7">
                <div className="overline">{p.category}</div>
                <div className="font-serif-display text-3xl mt-2 text-slate-900 group-hover:text-sky-500 transition-colors">{p.name}</div>
                <div className="text-slate-600 text-sm mt-2">{p.tagline}</div>
                <div className="mt-5 flex items-end justify-between">
                  <div>
                    <div className="overline">{t.product.from}</div>
                    <div className="font-serif-display text-3xl text-sky-600">${p.base_price?.toLocaleString()}</div>
                  </div>
                  <div className="text-xs uppercase tracking-[0.2em] font-ui text-slate-700 group-hover:text-sky-500">Configure →</div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <LearnSection t={t} />
      <InstagramSection posts={igPosts} />
      <BlogSection blog={blog} />
    </main>
  );
}

function Stat({ k, v }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-ui">{k}</div>
      <div className="font-serif-display text-3xl text-slate-900 mt-1">{v}</div>
    </div>
  );
}

function USPCard({ num, title, sub, body, highlight }) {
  return (
    <div className={`p-10 rounded-md border ${highlight ? "bg-gradient-to-br from-sky-50 to-cyan-50 border-sky-200" : "bg-white border-slate-200"} transition-all hover:shadow-xl hover:-translate-y-1`}>
      <div className="overline">{num}</div>
      <div className="font-serif-display text-7xl text-sky-600 mt-3">{title}</div>
      <div className="text-xs uppercase tracking-widest text-slate-500 mt-2">{sub}</div>
      <p className="text-slate-700 mt-6 leading-relaxed">{body}</p>
    </div>
  );
}
